import React, { useState, useEffect } from 'react';
import SubAdminModal from '../../components/shared/subAdminModal';
import DeleteIcon from '../../assets/icons/delete.svg';
import ConfirmationModal from '../../components/modals/confirmationModal';
import AccessManagementTable from '../../components/shared/AccessManagementTable';
import AuditLogsTable from '../../components/shared/AuditLogsTable';
import Pagination from '../../components/shared/Pagination'; // Import Pagination component
import { AdminService } from '../../services';
import Modal from '../../components/modals/modal';
import SuccessIcon from '../../assets/icons/profile-updated-modal-icon.svg'; // New import for the success icon
import { Toast } from '../../components/shared/Toast';
import 'react-loading-skeleton/dist/skeleton.css';
import AccessManagementTableSkeleton from '../../components/shared/AccessManagementTableSkeleton';

const styles = {
	mainButton:
		'inline-flex items-center space-x-2 bg-dark-blue max-w-[210px] h-[52px] py-5 px-4 text-base font-semibold leading-100 text-white rounded-md cursor-pointer'
};

export default function AccessManagementPage() {
	const [subAdminModalOpen, setSubAdminModalOpen] = useState(false);
	const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
	const [isModalEditMode, setIsModalEditMode] = useState(false);
	const [currentSubAdminData, setCurrentSubAdminData] = useState(null);
	const [subAdminToDelete, setSubAdminToDelete] = useState(null);

	// Pagination states for Access Management Table
	const [currentPageAccess, setCurrentPageAccess] = useState(1);
	const docsPerPageAccess = 10; // 10 entries for access management page

	// Pagination states for Audit Logs Table
	const [currentPageAudit, setCurrentPageAudit] = useState(1);
	const docsPerPageAudit = 5; // 5 entries for audit logs table

	const [accessUsers, setAccessUsers] = useState([]);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [modalMessage, setModalMessage] = useState('');
	const [modalHeading, setModalHeading] = useState(''); // New state for dynamic modal heading
	const [isDeleting, setIsDeleting] = useState(false); // New state for delete loading
	const [isLoadingData, setIsLoadingData] = useState(true); // New state for data loading

	// Apply pagination to accessUsers
	const indexOfLastAccessUser = currentPageAccess * docsPerPageAccess;
	const indexOfFirstAccessUser = indexOfLastAccessUser - docsPerPageAccess;
	const currentAccessUsers = accessUsers.slice(indexOfFirstAccessUser, indexOfLastAccessUser);

	const auditLogs = [
		{
			activity: 'Logged in',
			comment: 'Logged into the sub-admin',
			email: 'majidali@nesl-it.com',
			datetime: '12/10/2025, 2:30 PM'
		},
		{
			activity: 'Resolve query',
			comment: 'Resolved query #0981',
			email: 'muhammadhaseeb@nesl-it.com',
			datetime: '12/12/2025, 9:23 AM'
		},
		{
			activity: 'User Registration',
			comment: 'New user registered',
			email: 'alex.brown@example.com',
			datetime: '11/28/2025, 11:00 AM'
		},
		{
			activity: 'Document Deleted',
			comment: 'Document removed from system',
			email: 'lisa.davis@example.com',
			datetime: '11/22/2025, 3:30 PM'
		},
		{
			activity: 'Settings Updated',
			comment: 'System settings modified',
			email: 'admin@example.com',
			datetime: '10/25/2025, 1:15 PM'
		}
	];

	// Apply pagination to auditLogs
	const indexOfLastAuditLog = currentPageAudit * docsPerPageAudit;
	const indexOfFirstAuditLog = indexOfLastAuditLog - docsPerPageAudit;
	const currentAuditLogs = auditLogs.slice(indexOfFirstAuditLog, indexOfLastAuditLog);

	useEffect(() => {
		fetchAccessUsers();
	}, []);

	const fetchAccessUsers = async () => {
		setIsLoadingData(true); // Set loading to true
		try {
			const response = await AdminService.getSubAdmins();
			const subAdmins = response.data.users;
			const users = subAdmins.map((subAdmin) => ({
				name: subAdmin.firstName + ' ' + subAdmin.lastName,
				email: subAdmin.email,
				role: subAdmin.roleType || '',
				status: subAdmin.status,
				_id: subAdmin._id, // Include the user ID for edit/delete operations
				firstName: subAdmin.firstName, // Add firstName directly
				lastName: subAdmin.lastName // Add lastName directly
			}));
			setAccessUsers(users);
		} catch (error) {
			console.error('Error fetching sub-admins:', error);
		} finally {
			setIsLoadingData(false); // Set loading to false
		}
	};

	const handleAddSubAdmin = async (newSubAdminData) => {
		try {
			const res = await AdminService.addSubAdmin(newSubAdminData);
			if (res.status === 200) {
				const { firstName, lastName } = newSubAdminData;
				setModalMessage(
					<>
						User "<span className="text-dark-blue font-medium">{`${firstName} ${lastName}`}</span>"
						added successfully
					</>
				);
				setModalHeading('User added successfully'); // Set heading for successful add
				setShowSuccessModal(true);
				fetchAccessUsers(); // Refresh the list of sub-admins
				return true; // Indicate success
			} else {
				setModalMessage(res.message || 'Failed to add user.');
				setShowErrorModal(true);
				return false; // Indicate failure
			}
		} catch (error) {
			console.error('Error adding user:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			setModalMessage(errorMessage);
			setShowErrorModal(true);
			return false; // Indicate failure
		}
	};

	const handleUpdateSubAdmin = async (subAdminId, dataToUpdate) => {
		try {
			// Ensure we have a valid userId before proceeding
			if (!subAdminId) {
				console.error('User ID is missing for update.');
				Toast.error('Error: User ID is missing for update.');
				return false;
			}

			// Validate first name and last name length
			if (dataToUpdate.firstName && dataToUpdate.firstName.length < 3) {
				Toast.error('Minimum length for first name is 3 characters');
				return false;
			}
			if (dataToUpdate.lastName && dataToUpdate.lastName.length < 3) {
				Toast.error('Minimum length for last name is 3 characters');
				return false;
			}

			const res = await AdminService.updateSubAdmin(subAdminId, dataToUpdate);
			if (res.status === 200) {
				// Determine if it was an update or deactivation
				if (dataToUpdate.status === 'inactive') {
					const subAdminName = currentSubAdminData?.name || 'Sub-admin';
					setModalMessage(
						<>
							User "<span className="text-dark-blue font-medium">{subAdminName}</span>" deactivated
							successfully
						</>
					);
					setModalHeading('User deactivated successfully'); // Set heading for deactivation
				} else {
					// For updates, we use the firstName and lastName from dataToUpdate
					setModalMessage(
						<>
							User "
							<span className="text-dark-blue font-medium">{`${
								dataToUpdate.firstName || currentSubAdminData?.firstName
							} ${dataToUpdate.lastName || currentSubAdminData?.lastName}`}</span>
							" updated successfully
						</>
					);
					setModalHeading('User updated successfully'); // Set heading for update
				}
				setShowSuccessModal(true);
				fetchAccessUsers(); // Refresh the list of sub-admins
				return true; // Indicate success
			} else {
				Toast.error(res.message || 'Failed to update.');
				return false; // Indicate failure
			}
		} catch (error) {
			console.error('Error updating user:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			Toast.error(errorMessage);
			return false; // Indicate failure
		}
	};

	const handleDeleteSubAdminConfirm = async () => {
		if (!subAdminToDelete?._id) {
			console.error('User ID is missing for deletion.');
			setModalMessage('Error: User ID is missing for deletion.');
			setModalHeading('Deletion Failed');
			setShowErrorModal(true);
			setDeleteConfirmationModalOpen(false);
			return;
		}

		setIsDeleting(true);
		try {
			const res = await AdminService.deleteSubAdmin(subAdminToDelete._id);

			if (res.status === 200) {
				setModalMessage(
					<>
						User "
						<span className="text-dark-blue font-medium">
							{subAdminToDelete.firstName} {subAdminToDelete.lastName}
						</span>
						" deleted successfully
					</>
				);
				setModalHeading('User deleted successfully');
				setShowSuccessModal(true);
				fetchAccessUsers(); // Refresh the list
			} else {
				Toast.error(res.message || 'Failed to delete user.');
			}
		} catch (error) {
			console.error('Error deleting user:', error);
			const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
			Toast.error(errorMessage);
		} finally {
			setIsDeleting(false);
			setDeleteConfirmationModalOpen(false);
		}
	};

	const handleEditSubAdmin = (user) => {
		setSubAdminModalOpen(true);
		setIsModalEditMode(true);
		setCurrentSubAdminData(user);
	};

	const handleDeleteSubAdmin = (user) => {
		setDeleteConfirmationModalOpen(true);
		setSubAdminToDelete(user);
	};

	return (
		<div className="md:py-4 bg-bgColor">
			<div className="flex justify-between flex-col md:flex-row items-center mb-12">
				<div>
					<h1 className="text-dark-blue font-bold text-main leading-100 md:mb-0 mb-5">
						Access Management
					</h1>
				</div>

				<div>
					<button
						className={styles.mainButton}
						onClick={() => {
							setSubAdminModalOpen(true);
							setIsModalEditMode(false);
							setCurrentSubAdminData(null);
						}}
					>
						<svg
							width="12"
							height="12"
							viewBox="0 0 12 12"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M6.5625 0.875C6.5625 0.391016 6.17148 0 5.6875 0C5.20352 0 4.8125 0.391016 4.8125 0.875V4.8125H0.875C0.391016 4.8125 0 5.20352 0 5.6875C0 6.17148 0.391016 6.5625 0.875 6.5625H4.8125V10.5C4.8125 10.984 5.20352 11.375 5.6875 11.375C6.17148 11.375 6.5625 10.984 6.5625 10.5V6.5625H10.5C10.984 6.5625 11.375 6.17148 11.375 5.6875C11.375 5.20352 10.984 4.8125 10.5 4.8125H6.5625V0.875Z"
								fill="white"
							/>
						</svg>
						<span>Add User</span>
					</button>
				</div>
			</div>

			<h2 className="text-[22px] leading-100 font-medium text-dark-blue mb-12">
				Manage platform users, roles, and access permissions.
			</h2>

			{isLoadingData ? (
				<AccessManagementTableSkeleton docsPerPage={docsPerPageAccess} />
			) : currentAccessUsers.length > 0 ? (
				<AccessManagementTable
					accessUsers={currentAccessUsers}
					onEditSubAdmin={handleEditSubAdmin}
					onDeleteSubAdmin={handleDeleteSubAdmin}
				/>
			) : (
				<div className="flex justify-center items-center h-40 text-lg text-dark-blue">
					No Users found.
				</div>
			)}

			{currentAccessUsers.length > 0 && (
				<Pagination
					totalDocs={accessUsers.length}
					docsPerPage={docsPerPageAccess}
					onPageChange={setCurrentPageAccess}
				/>
			)}

			<AuditLogsTable heading={'Audit Logs'} auditLogs={currentAuditLogs} />
			<Pagination
				totalDocs={auditLogs.length}
				docsPerPage={docsPerPageAudit}
				onPageChange={setCurrentPageAudit}
			/>

			<SubAdminModal
				isOpen={subAdminModalOpen}
				onClose={() => setSubAdminModalOpen(false)}
				isEdit={isModalEditMode}
				initialFirstName={currentSubAdminData?.firstName} // Use direct firstName
				initialLastName={currentSubAdminData?.lastName} // Use direct lastName
				initialEmail={currentSubAdminData?.email}
				userId={currentSubAdminData?._id} // Pass the userId to the modal
				currentStatus={currentSubAdminData?.status} // Pass current status to the modal
				initialRole={currentSubAdminData?.role} // Pass the user's current role
				onAddSubAdmin={handleAddSubAdmin} // Pass the handler here
				onUpdateSubAdmin={handleUpdateSubAdmin} // New prop for updating a sub-admin (now handles deactivation)
			/>

			<ConfirmationModal
				isOpen={deleteConfirmationModalOpen}
				onClose={() => setDeleteConfirmationModalOpen(false)}
				icon={<img src={DeleteIcon} alt="Delete" className="mx-auto" />}
				heading="Deleting User"
				description={
					<em className="italic text-xs sm:text-base">
						Are you sure you want to delete{' '}
						<span className="text-dark-blue font-medium italic">{subAdminToDelete?.name}</span> from
						users?
					</em>
				}
				primaryButtonText="Cancel"
				secondaryButtonText="Delete"
				onPrimaryClick={() => setDeleteConfirmationModalOpen(false)}
				onSecondaryClick={handleDeleteSubAdminConfirm} // Call the new handler
				isLoading={isDeleting} // Pass loading state
			/>

			<Modal
				isOpen={showSuccessModal}
				icon={<img src={SuccessIcon} alt="Success" className="mx-auto" />}
				heading={modalHeading} // Use the dynamic heading state
				description={modalMessage}
				buttonText="View Users"
				onButtonClick={() => {
					setShowSuccessModal(false);
				}} // No navigation here, stay on the same page
			/>

			<Modal
				isOpen={showErrorModal}
				heading="Error"
				description={modalMessage}
				buttonText="Got it"
				onButtonClick={() => setShowErrorModal(false)}
			/>
		</div>
	);
}
